import { useCallback, useEffect, useState } from "react";
import { Save, Send } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { DEFAULT_SMTP_SETTINGS, type GenericResponse, type SmtpSettings } from "@/lib/types";

export function SmtpPage() {
  const [cfg, setCfg] = useState<SmtpSettings>(DEFAULT_SMTP_SETTINGS);
  const [testTo, setTestTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setCfg(await api.get<SmtpSettings>("/api/settings/smtp"));
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = async (): Promise<boolean> => {
    try {
      const resp = await api.post<GenericResponse>("/api/settings/smtp", cfg);
      setFeedback({ kind: resp.success ? "success" : "error", text: resp.message });
      return resp.success;
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
      return false;
    }
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    await save();
    setSaving(false);
  };

  const onTest = async () => {
    setTesting(true);
    setFeedback(null);
    // Erst speichern, dann testen — sonst testet man alte Settings.
    if (await save()) {
      try {
        const resp = await api.post<GenericResponse>("/api/settings/smtp/test", { to: testTo });
        setFeedback({ kind: resp.success ? "success" : "error", text: resp.message });
      } catch (e) {
        setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Versand fehlgeschlagen") });
      }
    }
    setTesting(false);
  };

  const field =
    "h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary";

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title={t("SMTP-Server")}
        description={t("Globale Versand-Einstellungen für E-Mail-Alarm-Kanäle. Die Empfänger legst du pro Kanal unter Alarmierung fest.")}
      >
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : (
          <form onSubmit={onSave} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Host
              </span>
              <input
                type="text"
                value={cfg.host}
                onChange={(e) => setCfg({ ...cfg, host: e.target.value })}
                placeholder="mail.werk.example"
                autoComplete="off"
                className={field + " font-mono text-xs"}
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Port
                </span>
                <input
                  type="number"
                  min={1}
                  max={65535}
                  value={cfg.port}
                  onChange={(e) => setCfg({ ...cfg, port: Number(e.target.value) })}
                  className={field + " font-mono text-xs"}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t("Verschlüsselung")}
                </span>
                <select
                  value={cfg.security}
                  onChange={(e) => setCfg({ ...cfg, security: e.target.value as SmtpSettings["security"] })}
                  className={field + " cursor-pointer"}
                >
                  <option value="starttls">STARTTLS (587)</option>
                  <option value="tls">TLS (465)</option>
                  <option value="none">{t("Keine (25)")}</option>
                </select>
              </label>
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Benutzername")}
              </span>
              <input
                type="text"
                value={cfg.username}
                onChange={(e) => setCfg({ ...cfg, username: e.target.value })}
                autoComplete="off"
                className={field + " font-mono text-xs"}
              />
              <span className="text-[11px] text-muted-foreground">{t("leer = keine Anmeldung")}</span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Passwort")}
              </span>
              <input
                type="password"
                value={cfg.password}
                onChange={(e) => setCfg({ ...cfg, password: e.target.value })}
                autoComplete="new-password"
                className={field + " font-mono text-xs"}
              />
            </label>

            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Absender (Von)")}
              </span>
              <input
                type="text"
                value={cfg.from}
                onChange={(e) => setCfg({ ...cfg, from: e.target.value })}
                placeholder="Leif <leif@werk.example>"
                autoComplete="off"
                className={field + " max-w-lg font-mono text-xs"}
              />
            </label>

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={saving || testing}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Einstellungen speichern")}
              </button>
            </div>
          </form>
        )}
      </Card>

      <Card
        title={t("Test-E-Mail")}
        description={t("Speichert die Einstellungen und verschickt eine Testnachricht.")}
      >
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex min-w-64 flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Empfänger")}
            </span>
            <input
              type="text"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="du@werk.example"
              autoComplete="off"
              className={field + " font-mono text-xs"}
            />
          </label>
          <button
            type="button"
            onClick={() => void onTest()}
            disabled={testing || saving || !testTo.trim()}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-muted disabled:opacity-60"
          >
            <Send className="h-3.5 w-3.5" /> {testing ? t("Sende…") : t("Test senden")}
          </button>
        </div>
      </Card>
    </div>
  );
}
